(() => {
  if (window.__btkV2HeroLoaded) return;
  window.__btkV2HeroLoaded = true;

  const ROOT_SELECTOR = '[data-btk-v2-hero]';

  function preloadHeroImages(root) {
    if (
      !root ||
      root.dataset.btkV2ImagesPreloaded ===
        'true'
    ) {
      return;
    }

    root.dataset.btkV2ImagesPreloaded =
      'true';

    const images = Array.from(
      root.querySelectorAll('img')
    );

    const preloaders = [];

    images.forEach((imageElement) => {
      imageElement.loading = 'eager';

      const isPrimaryHeroImage =
        imageElement.classList.contains(
          'btk-v2-hero__image'
        );

      if (
        isPrimaryHeroImage &&
        'fetchPriority' in imageElement
      ) {
        imageElement.fetchPriority = 'high';
      }

      const source =
        imageElement.currentSrc ||
        imageElement.src;

      if (!source) return;

      const preloader = new Image();

      preloader.loading = 'eager';

      if (
        isPrimaryHeroImage &&
        'fetchPriority' in preloader
      ) {
        preloader.fetchPriority = 'high';
      }

      if (imageElement.sizes) {
        preloader.sizes =
          imageElement.sizes;
      }

      if (imageElement.srcset) {
        preloader.srcset =
          imageElement.srcset;
      }

      preloader.src = source;

      if (
        typeof preloader.decode ===
        'function'
      ) {
        preloader
          .decode()
          .catch(() => {});
      }

      preloaders.push(preloader);
    });

    root.__btkV2HeroImagePreloaders =
      preloaders;
  }

  function initHeroVideos(root) {
    const videos = Array.from(
      root.querySelectorAll(
        '.btk-v2-hero__video'
      )
    );

    if (!videos.length) return;

    const soundButtons = Array.from(
      root.querySelectorAll(
        '[data-btk-v2-sound-button]'
      )
    );

    const autoplayEnabled =
      new WeakMap();

    const autoplayAttempted =
      new WeakSet();

    const internallyPaused =
      new WeakSet();

    const resumeAfterVisibility =
      new WeakSet();

    let resizeTimer = null;
    let soundUnlocked = false;
    let userPaused = false;

    /*
      Save the original Shopify autoplay
      setting, then remove the native autoplay
      attribute.

      From this point forward, this JS is the
      only thing allowed to initiate autoplay.
      This prevents browsers from restarting
      the video later on their own.
    */
    videos.forEach((video) => {
      autoplayEnabled.set(
        video,
        video.autoplay
      );

      video.autoplay = false;
      video.removeAttribute(
        'autoplay'
      );
    });

    function isVideoVisible(video) {
      const media =
        video.closest(
          '.btk-v2-hero__media'
        );

      if (!media) return false;

      const styles =
        window.getComputedStyle(media);

      return (
        styles.display !== 'none' &&
        styles.visibility !== 'hidden'
      );
    }

    function getVideoForButton(button) {
      const frame =
        button.closest(
          '.btk-v2-hero__media-frame'
        );

      if (!frame) return null;

      return frame.querySelector(
        '.btk-v2-hero__video'
      );
    }

    function updateSoundButtons() {
      soundButtons.forEach((button) => {
        const video =
          getVideoForButton(button);

        const shouldShow =
          video &&
          isVideoVisible(video) &&
          video.muted;

        button.hidden =
          !shouldShow;
      });
    }

    function pauseVideoInternally(video) {
      if (
        !video ||
        video.paused
      ) {
        return;
      }

      internallyPaused.add(video);
      video.pause();
    }

    async function autoplayVideo(video) {
      const shouldAutoplay =
        autoplayEnabled.get(video) ===
        true;

      if (!shouldAutoplay) {
        video.muted = false;
        updateSoundButtons();
        return;
      }

      /*
        Never restart a video after the
        customer manually paused it.
      */
      if (userPaused) {
        updateSoundButtons();
        return;
      }

      /*
        Autoplay is attempted only once per
        desktop/mobile video element.

        Resize, scrolling and tab switching
        cannot trigger autoplay again.
      */
      if (
        autoplayAttempted.has(video)
      ) {
        updateSoundButtons();
        return;
      }

      autoplayAttempted.add(video);

      /*
        If the browser already started it
        before JS initialized, leave the
        playback state alone.
      */
      if (!video.paused) {
        updateSoundButtons();
        return;
      }

      if (soundUnlocked) {
        video.muted = false;

        try {
          await video.play();
        } catch (error) {
          /*
            Ignore playback restrictions.
          */
        }

        updateSoundButtons();
        return;
      }

      /*
        First try autoplay with sound.
      */
      video.muted = false;

      try {
        await video.play();

        soundUnlocked = true;
        updateSoundButtons();
        return;
      } catch (error) {
        /*
          Browser blocked autoplay with sound.
        */
      }

      /*
        Fall back to muted autoplay.

        The TAP FOR SOUND button becomes
        available after this.
      */
      video.muted = true;

      try {
        await video.play();
      } catch (error) {
        /*
          Some browser/device settings may
          block autoplay completely.
        */
      }

      updateSoundButtons();
    }

    async function enableSound(button) {
      const video =
        getVideoForButton(button);

      if (
        !video ||
        !isVideoVisible(video)
      ) {
        return;
      }

      /*
        Clicking TAP FOR SOUND is an explicit
        customer action.

        Restart the video from the beginning
        so the customer hears the full video
        instead of joining midway through.
      */
      userPaused = false;
      soundUnlocked = true;

      video.muted = false;
      video.volume = 1;
      video.currentTime = 0;

      button.hidden = true;

      try {
        await video.play();
      } catch (error) {
        /*
          Native video controls remain
          available if playback is blocked.
        */
      }

      updateSoundButtons();
    }

    function handleVideoPause(event) {
      const video =
        event.currentTarget;

      /*
        Ignore pauses caused by our own
        responsive/tab-management logic.
      */
      if (
        internallyPaused.has(video)
      ) {
        internallyPaused.delete(video);
        return;
      }

      /*
        A pause on the visible video while
        the page is active came from the
        customer or browser media controls.

        Respect it until the customer
        explicitly presses Play again.
      */
      if (
        !document.hidden &&
        isVideoVisible(video)
      ) {
        userPaused = true;
      }
    }

    function handleVideoPlay(event) {
      const video =
        event.currentTarget;

      /*
        If the customer presses the native
        Play control, playback is allowed
        again.
      */
      if (
        !document.hidden &&
        isVideoVisible(video)
      ) {
        userPaused = false;
      }
    }

    function syncVideos() {
      videos.forEach((video) => {
        if (!isVideoVisible(video)) {
          pauseVideoInternally(video);
          return;
        }

        /*
          If the customer paused the video,
          resize/scroll must not restart it.
        */
        if (userPaused) {
          return;
        }

        /*
          This will only autoplay a video
          whose initial autoplay has never
          been attempted.
        */
        autoplayVideo(video);
      });

      updateSoundButtons();
    }

    function handleResize() {
      if (resizeTimer) {
        window.clearTimeout(
          resizeTimer
        );
      }

      resizeTimer =
        window.setTimeout(
          syncVideos,
          100
        );
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        videos.forEach((video) => {
          /*
            Only remember videos that were
            actually playing before the tab
            became hidden.
          */
          if (
            isVideoVisible(video) &&
            !video.paused &&
            !userPaused
          ) {
            resumeAfterVisibility.add(
              video
            );

            pauseVideoInternally(
              video
            );
          } else {
            resumeAfterVisibility.delete(
              video
            );
          }
        });

        return;
      }

      /*
        When returning to the tab, resume
        ONLY a video that was playing before
        the tab was hidden.

        A manually paused video stays paused.
      */
      videos.forEach((video) => {
        if (
          !resumeAfterVisibility.has(
            video
          )
        ) {
          return;
        }

        resumeAfterVisibility.delete(
          video
        );

        if (
          userPaused ||
          !isVideoVisible(video)
        ) {
          return;
        }

        video
          .play()
          .catch(() => {});
      });

      updateSoundButtons();
    }

    function handleSoundButtonClick(
      event
    ) {
      enableSound(
        event.currentTarget
      );
    }

    soundButtons.forEach((button) => {
      button.addEventListener(
        'click',
        handleSoundButtonClick
      );
    });

    videos.forEach((video) => {
      video.addEventListener(
        'pause',
        handleVideoPause
      );

      video.addEventListener(
        'play',
        handleVideoPlay
      );

      video.addEventListener(
        'volumechange',
        updateSoundButtons
      );
    });

    window.addEventListener(
      'resize',
      handleResize
    );

    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    );

    /*
      Initial autoplay happens here once.
    */
    syncVideos();

    root.__btkV2HeroVideoDestroy =
      () => {
        if (resizeTimer) {
          window.clearTimeout(
            resizeTimer
          );

          resizeTimer = null;
        }

        window.removeEventListener(
          'resize',
          handleResize
        );

        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );

        soundButtons.forEach(
          (button) => {
            button.removeEventListener(
              'click',
              handleSoundButtonClick
            );
          }
        );

        videos.forEach((video) => {
          video.removeEventListener(
            'pause',
            handleVideoPause
          );

          video.removeEventListener(
            'play',
            handleVideoPlay
          );

          video.removeEventListener(
            'volumechange',
            updateSoundButtons
          );

          video.pause();
        });
      };
  }

  function initReviews(root) {
    const reviewsContainer =
      root.querySelector(
        '[data-btk-v2-reviews]'
      );

    if (!reviewsContainer) return;

    const reviews = Array.from(
      reviewsContainer.querySelectorAll(
        '[data-btk-v2-review]'
      )
    );

    const dots = Array.from(
      reviewsContainer.querySelectorAll(
        '[data-btk-v2-review-dot]'
      )
    );

    if (!reviews.length || !dots.length) {
      return;
    }

    let activeIndex = 0;

    function showReview(index) {
      if (
        index < 0 ||
        index >= reviews.length
      ) {
        return;
      }

      activeIndex = index;

      reviews.forEach(
        (review, reviewIndex) => {
          review.classList.toggle(
            'is-active',
            reviewIndex === activeIndex
          );
        }
      );

      dots.forEach(
        (dot, dotIndex) => {
          const isActive =
            dotIndex === activeIndex;

          dot.classList.toggle(
            'is-active',
            isActive
          );

          dot.setAttribute(
            'aria-selected',
            isActive ? 'true' : 'false'
          );
        }
      );
    }

    dots.forEach((dot) => {
      dot.addEventListener(
        'click',
        () => {
          const index = Number(
            dot.dataset.index
          );

          if (Number.isNaN(index)) {
            return;
          }

          showReview(index);
        }
      );
    });

    showReview(activeIndex);
  }

  function initSection(root) {
    if (
      !root ||
      root.dataset.btkV2Initialized ===
        'true'
    ) {
      return;
    }

    root.dataset.btkV2Initialized =
      'true';

    preloadHeroImages(root);
    initHeroVideos(root);
    initReviews(root);
  }

  function initAllSections(
    scope = document
  ) {
    if (
      scope.matches?.(ROOT_SELECTOR)
    ) {
      initSection(scope);
      return;
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initSection);
  }

  /*
    This file is loaded with `defer`, so the
    section markup already exists when this
    runs. Initialize immediately instead of
    waiting for DOMContentLoaded.
  */
  initAllSections();

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAllSections(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        event.target.matches?.(
          ROOT_SELECTOR
        )
          ? event.target
          : event.target.querySelector(
              ROOT_SELECTOR
            );

      if (
        root &&
        typeof root.__btkV2HeroVideoDestroy ===
          'function'
      ) {
        root.__btkV2HeroVideoDestroy();
      }
    }
  );
})();